import React from "react";
import { Trash2 } from "lucide-react";
import { AudioChat, VideoChat, TextChat, ImageChat } from "src/types";
import { getAssetUrl } from "@utils/id.generator";
import Layout from "@components/layout";

type Props = {
  series: AudioChat | VideoChat | TextChat | ImageChat;
  onUpdate: (updated: AudioChat | VideoChat | TextChat | ImageChat) => void;
};

const ChatSeriesEditor: React.FC<Props> = ({ series, onUpdate }) => {
  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...series.options];
    updatedOptions[index] = { ...updatedOptions[index], text: value };
    onUpdate({ ...series, options: updatedOptions });
  };

  const toggleCorrectAnswer = (optionId: string) => {
    const isSelected = series.answer.includes(optionId);
    const updatedAnswers = isSelected
      ? series.answer.filter((id) => id !== optionId)
      : [...series.answer, optionId];
    onUpdate({ ...series, answer: updatedAnswers });
  };

  const removeOption = (index: number) => {
    const updatedOptions = series.options.filter((_, i) => i !== index);
    const updatedAnswers = series.answer.filter(
      (id) => id !== series.options[index].id
    );
    onUpdate({ ...series, options: updatedOptions, answer: updatedAnswers });
  };

  const moveOption = (from: number, to: number) => {
    const options = [...series.options];
    const [moved] = options.splice(from, 1);
    options.splice(to, 0, moved);
    onUpdate({ ...series, options });
  };

  const addOption = () => {
    const newOption = { id: `${Date.now()}`, text: "" };
    onUpdate({ ...series, options: [...series.options, newOption] });
  };

  const handleQuestionChange = (value: string) => {
    if (series.title === "TEXTCHAT") {
      const updatedSeries: TextChat = {
        ...series,
        question: { text: value },
      };
      onUpdate(updatedSeries);
    } else if (series.title === "AUDIOCHAT") {
      const updatedSeries: AudioChat = {
        ...series,
        question: { audioUrl: value },
      };
      onUpdate(updatedSeries);
    } else if (series.title === "VIDEOCHAT") {
      const updatedSeries: VideoChat = {
        ...series,
        question: { videoUrl: value },
      };
      onUpdate(updatedSeries);
    } else {
      const updatedSeries: ImageChat = {
        ...series,
        question: { imageUrl: value },
      } as any;
      onUpdate(updatedSeries);
    }
  };

  const handleSubmit = async () => {
    try {
      const endpoint = series.id
        ? `/api/series/${series.id}`
        : `/api/lessons/series`;

      console.log("Submitting series:", series);

      //   const method = series.id ? "PATCH" : "POST";

      //   const response = await fetch(endpoint, {
      //     method,
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify(series),
      //   });

      //   const result = await response.json();

      //   if (result.status === "success") {
      //     alert("Series saved successfully!");
      //   } else {
      //     alert("Failed to save series.");
      //   }
    } catch (err) {
      alert("Server error occurred.");
    }
  };

  return (
    <div className="space-y-6 bg-gray-50 p-6 rounded-xl shadow-lg">
      <h2 className="text-xl font-bold text-gray-800">{series.title} Editor</h2>
      {/* Question Input */}
      <div>
        <label className="block font-semibold text-gray-700 mb-1">
          Question
        </label>
        <input
          type="text"
          value={
            series.title === "TEXTCHAT"
              ? series.question.text
              : series.title === "AUDIOCHAT"
              ? series.question.audioUrl
              : series.title === "VIDEOCHAT"
              ? series.question.videoUrl
              : series.question.imageUrl
          }
          onChange={(e) => handleQuestionChange(e.target.value)}
          className="form-input w-full"
          placeholder={
            series.title === "TEXTCHAT"
              ? "Enter chat text"
              : series.title === "AUDIOCHAT"
              ? "Enter audio code"
              : series.title === "VIDEOCHAT"
              ? "Enter video code"
              : "Enter image code"
          }
        />
        {/* Media Preview */}
        {series.title === "AUDIOCHAT" && series.question.audioUrl && (
          <audio
            controls
            src={getAssetUrl("audio", series.question.audioUrl)}
            className="mt-3 w-full rounded"
          />
        )}
        {series.title === "VIDEOCHAT" && series.question.videoUrl && (
          <video
            controls
            src={getAssetUrl("video", series.question.videoUrl)}
            className="mt-3 w-full rounded h-[16rem] object-contain"
          />
        )}

        {series.title === "IMAGECHAT" && series.question.imageUrl && (
          <img
            src={getAssetUrl("graphic", series.question.imageUrl)}
            alt="Chat graphics"
            className="mt-3 w-full rounded max-h-[12rem] object-contain"
          />
        )}
      </div>
      {/* Options */}
      <div>
        <label className="block font-semibold text-gray-700 mb-2">
          Options
        </label>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {series.options.map((opt, index) => (
            <div
              key={opt.id}
              className="min-w-[250px] flex-shrink-0 bg-gray-50 border rounded-md p-4 shadow-sm space-y-3"
            >
              <input
                type="text"
                value={opt.text || ""}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                className="form-input w-full"
                placeholder={`Option ${index + 1}`}
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={series.answer.includes(opt.id)}
                    onChange={() => toggleCorrectAnswer(opt.id)}
                  />
                  Correct
                </label>
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  className="text-red-500 hover:text-red-700"
                  title="Remove option"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <button
                  disabled={index === 0}
                  onClick={() => moveOption(index, index - 1)}
                  className="hover:text-gray-700"
                >
                  ↑ Move
                </button>
                <button
                  disabled={index === series.options.length - 1}
                  onClick={() => moveOption(index, index + 1)}
                  className="hover:text-gray-700"
                >
                  ↓ Move
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addOption}
          className="text-brand text-sm hover:underline mt-2"
        >
          + Add Option
        </button>

        <div className="pt-4">
          <button
            type="button"
            onClick={handleSubmit}
            className="px-5 py-2 bg-brand w-full text-white rounded-md hover:bg-brand-dark transition"
          >
            Save Serie
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatSeriesEditor;
